import React, { useState, useEffect } from 'react';
import { BookOpen } from 'lucide-react';
import { UserGuideModal } from './UserGuideModal';
import { useAuth } from '../contexts/AuthContext';
import { userGuideService } from '../services/userGuideService';
import { userGuideCategories } from '../content/userGuideData';

export const UserGuideButton: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadProgress();
    }
  }, [user]);

  const loadProgress = async () => {
    if (!user) return;

    const progressMap = await userGuideService.getUserProgress(user.id);
    const completed = new Set<string>();

    progressMap.forEach((progress) => {
      if (progress.completed) completed.add(progress.articleId);
    });

    const totalArticles = userGuideCategories.reduce(
      (sum, cat) => sum + cat.articles.length,
      0
    );
    const completedCount = completed.size;
    const percentage = totalArticles > 0 ? Math.round((completedCount / totalArticles) * 100) : 0;
    setProgressPercentage(percentage);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    loadProgress();
  };

  const getProgressColor = () => {
    if (progressPercentage >= 75) return 'bg-green-500';
    if (progressPercentage >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (!user) return null;

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all hover:scale-110 focus:outline-none focus:ring-4 focus:ring-blue-300 z-50 group"
        aria-label="Open User Guide"
        title="Open User Guide"
      >
        <BookOpen className="w-6 h-6" />
        <span className="absolute right-full mr-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          User Guide
        </span>
        {progressPercentage > 0 && (
          <span
            className={`absolute -top-1 -right-1 ${getProgressColor()} text-white text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center border-2 border-white shadow-md`}
          >
            {progressPercentage}%
          </span>
        )}
      </button>

      <UserGuideModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />
    </>
  );
};
